import { useState } from 'react'
import type { PluginSettings, ExifExtractorSettings } from '@/data/dropPlugins'
import { ProcessingSettings } from './ProcessingSettings'
import { ExifSettings } from './ExifSettings'

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

type ProcessingPreset = 'share' | 'print' | 'original' | 'custom'
type ExifPreset = 'where-when' | 'everything' | 'nothing' | 'custom'

const PROCESSING_PRESETS: Record<Exclude<ProcessingPreset, 'custom'>, PluginSettings> = {
  share: { format: 'webp', maxWidth: 2000, maxHeight: 2000, quality: 85 },
  print: { format: 'jpeg', quality: 95 },
  original: {},
}

const EXIF_PRESETS: Record<Exclude<ExifPreset, 'custom'>, ExifExtractorSettings> = {
  'where-when': {},  // defaults: location + dateTime = true, others = false
  everything: {
    fieldGroups: { location: true, dateTime: true, cameraInfo: true, shootingSettings: true },
  },
  nothing: {
    fieldGroups: { location: false, dateTime: false, cameraInfo: false, shootingSettings: false },
  },
}

// ---------------------------------------------------------------------------
// Preset detection from current settings
// ---------------------------------------------------------------------------

function detectProcessingPreset(settings: PluginSettings): ProcessingPreset {
  for (const [key, preset] of Object.entries(PROCESSING_PRESETS)) {
    if (settingsMatch(settings, preset)) return key as ProcessingPreset
  }
  return 'custom'
}

function detectExifPreset(settings: ExifExtractorSettings): ExifPreset {
  const fg = settings.fieldGroups ?? {}
  const loc = fg.location ?? true
  const dt = fg.dateTime ?? true
  const cam = fg.cameraInfo ?? false
  const shoot = fg.shootingSettings ?? false

  if (loc && dt && !cam && !shoot) return 'where-when'
  if (loc && dt && cam && shoot) return 'everything'
  if (!loc && !dt && !cam && !shoot) return 'nothing'
  return 'custom'
}

function settingsMatch(a: PluginSettings, b: PluginSettings): boolean {
  const normalize = (s: PluginSettings) => ({
    format: s.format ?? undefined,
    maxWidth: s.maxWidth ?? undefined,
    maxHeight: s.maxHeight ?? undefined,
    quality: s.quality ?? undefined,
  })
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PhotoSettingsProps {
  processingSettings: PluginSettings
  exifSettings: ExifExtractorSettings
  onProcessingChange: (settings: PluginSettings) => void
  onExifChange: (settings: ExifExtractorSettings) => void
  disabled?: boolean
}

const PROCESSING_OPTIONS = [
  { value: 'share' as const, label: 'Share online', description: 'Optimized for web and social media' },
  { value: 'print' as const, label: 'Print', description: 'High quality for printing' },
  { value: 'original' as const, label: 'Keep originals', description: 'No processing applied' },
] as const

const EXIF_OPTIONS = [
  { value: 'where-when' as const, label: 'Where & when', description: 'Location and date taken' },
  { value: 'everything' as const, label: 'Everything', description: 'All photo details' },
  { value: 'nothing' as const, label: 'Nothing', description: 'Strip for privacy' },
] as const

export function PhotoSettings({
  processingSettings,
  exifSettings,
  onProcessingChange,
  onExifChange,
  disabled,
}: PhotoSettingsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)

  const processingPreset = detectProcessingPreset(processingSettings)
  const exifPreset = detectExifPreset(exifSettings)

  const onProcessingPresetChange = (preset: ProcessingPreset) => {
    if (preset !== 'custom') {
      onProcessingChange(PROCESSING_PRESETS[preset])
    }
  }

  const onExifPresetChange = (preset: ExifPreset) => {
    if (preset !== 'custom') {
      onExifChange(EXIF_PRESETS[preset])
    }
  }

  return (
    <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-1">Photo Settings</h3>
        <p className="text-xs text-muted-foreground">Configure how uploaded photos are handled</p>
      </div>

      {/* Processing preset */}
      <div>
        <label className="block text-sm font-medium mb-3">How will these photos be used?</label>
        <div className="flex flex-wrap gap-2">
          {PROCESSING_OPTIONS.map((opt) => {
            const isActive = processingPreset === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onProcessingPresetChange(opt.value)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="font-medium">{opt.label}</div>
                <div className="text-xs opacity-75">{opt.description}</div>
              </button>
            )
          })}
          {processingPreset === 'custom' && (
            <div className="px-4 py-2 rounded-lg border border-primary bg-primary/10 text-primary text-sm">
              <div className="font-medium">Custom</div>
              <div className="text-xs opacity-75">See advanced settings</div>
            </div>
          )}
        </div>
      </div>

      {/* EXIF preset */}
      <div>
        <label className="block text-sm font-medium mb-3">What info should we save from each photo?</label>
        <div className="flex flex-wrap gap-2">
          {EXIF_OPTIONS.map((opt) => {
            const isActive = exifPreset === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onExifPresetChange(opt.value)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="font-medium">{opt.label}</div>
                <div className="text-xs opacity-75">{opt.description}</div>
              </button>
            )
          })}
          {exifPreset === 'custom' && (
            <div className="px-4 py-2 rounded-lg border border-primary bg-primary/10 text-primary text-sm">
              <div className="font-medium">Custom</div>
              <div className="text-xs opacity-75">See advanced settings</div>
            </div>
          )}
        </div>
      </div>

      {/* Advanced toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {showAdvanced ? '▾' : '▸'} Advanced settings
      </button>

      {showAdvanced && (
        <div className="space-y-4 pt-2 border-t border-border">
          <ProcessingSettings
            settings={processingSettings}
            onChange={onProcessingChange}
            disabled={disabled}
            bare
          />
          <ExifSettings
            settings={exifSettings}
            onChange={onExifChange}
            disabled={disabled}
            bare
          />
        </div>
      )}
    </div>
  )
}
